import { FC, useCallback } from 'react';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains';
import { NewFolderModal } from 'ui/indexPages/modals/NewFolderModal';
import {
  CreativeLibraryFolderModals,
  useCreativeLibraryFolderModals,
} from 'modules/creativeLibrary/hooks/useCreativeLibraryModals';
import { useSelectionContext } from 'modules/creativeLibrary/contexts/SelectionContext';
import { useAssetsQueryContext } from 'modules/creativeLibrary/contexts/AssetsQueryContext';
import { useTreeViewQuery } from 'modules/creativeLibrary/hooks/useTreeViewQuery';
import { useCreateAssetFolder } from 'modules/creativeLibrary/hooks/useCreateAssetFolder';
import { RenameFolderModal } from 'ui/indexPages/modals/RenameFolderModal';
import { useRenameAssetFolder } from 'modules/creativeLibrary/hooks/useRenameAssetFolder';
import { MoveToFolderModal } from 'ui/indexPages/modals/MoveToFolderModal';
import { isSelectionContent, isSelectionFolder, SelectionTypes } from 'utils/contexts/selection';
import { useMoveAssetContentsAndFoldersQuery } from 'modules/creativeLibrary/hooks/useMoveAssetContentsAndFoldersQuery';
import { useUploadAssetsContext } from 'modules/creativeLibrary/contexts/UploadAssetsContext';
import { DeleteItemsModal } from 'ui/indexPages/modals/DeleteItemsModal';
import { useDeleteAssetContentsAndFolders } from 'modules/creativeLibrary/hooks/useDeleteAssetContentsAndFolders';
import { getDeleteModalText } from 'modules/creativeLibrary/utils/modals';
import { MAX_FOLDER_NAME_LENGTH } from 'modules/creativeLibrary/constants';

export const CreativeLibraryModals: FC = () => {
  const { refetch, rootFolderId, folderId } = useAssetsQueryContext();
  const { active, clearAll } = useSelectionContext();
  const { pendingImages, updatePendingImagesDestination } = useUploadAssetsContext();
  const modals = useCreativeLibraryFolderModals();
  const createAssetFolder = useCreateAssetFolder();
  const { renameAssetFolder, isLoading: isLoadingRename } = useRenameAssetFolder();
  const moveItems = useMoveAssetContentsAndFoldersQuery();
  const { deleteItems } = useDeleteAssetContentsAndFolders();
  const haveSelectedFolderOnly = active.length === 1 && isSelectionFolder(active[0]);
  const selectedFolderData = haveSelectedFolderOnly ? active[0] : undefined;
  const selectedUploadedImages = pendingImages.filter((c) => c.isSelected && c.asset?.id);
  const selectedPendingImages = pendingImages.filter((c) => c.isSelected && !c.asset?.id);

  const selectedFolders = active.filter(isSelectionFolder);
  const seletedContent = active.filter(isSelectionContent);
  const selectedFolderIds = selectedFolders.map((c) => c.id);
  const selectedContentIds =
    selectedPendingImages.length > 0
      ? selectedPendingImages.map((_, i) => i) // just being used for content ids length
      : selectedUploadedImages.length > 0
        ? selectedUploadedImages.map((c) => c.asset!.id)
        : seletedContent.map((c) => c.id);

  /** Create Folder */
  const handleCreateFolderConfirmation = useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      modals.create.toggle(false);
      await createAssetFolder(newFolderName, newFolderDestination);
      refetch();
    },
    [createAssetFolder, refetch, modals.create],
  );

  /** Rename Folder */
  const handleRenameFolder = useCallback(
    async (newName: string) => {
      await renameAssetFolder(newName, selectedFolderData);
      modals.rename.toggle(false);
      refetch();
      clearAll();
    },
    [selectedFolderData, renameAssetFolder, modals.rename, refetch, clearAll],
  );

  /** Move Item **/
  const handleMoveToFolder = useCallback(
    async (
      folderIds: FolderId[],
      contentIds: number[],
      destination: AncestorIdData & AncestorNameData,
    ) => {
      if (selectedPendingImages.length > 0) {
        updatePendingImagesDestination(
          selectedPendingImages.map((img) => img.id),
          destination.id,
          destination.name,
        );
        modals.move.toggle(false);
        clearAll();
        return;
      }

      if (selectedUploadedImages.length > 0) {
        await moveItems({
          folderIds,
          contentIds,
          itemsToMove: selectedUploadedImages.map((c) => ({
            id: c.asset!.id,
            name: c.asset!.assetName,
            node: c.asset!,
            type: SelectionTypes.Content,
          })),
          destination,
        });
        updatePendingImagesDestination(
          selectedUploadedImages.map((img) => img.id),
          destination.id,
          destination.name,
        );
      } else {
        await moveItems({
          folderIds,
          contentIds,
          itemsToMove: active,
          destination,
        });
      }

      modals.move.toggle(false);
      refetch();
      clearAll();
    },
    [
      modals.move,
      refetch,
      clearAll,
      moveItems,
      active,
      selectedPendingImages,
      selectedUploadedImages,
      updatePendingImagesDestination,
    ],
  );

  /** Delete Item **/
  const handleDeleteToFolder = useCallback(async () => {
    await deleteItems({
      itemsToDelete: active,
    });
    modals.delete.toggle(false);
    refetch();
    clearAll();
  }, [
    modals.delete,
    refetch,
    clearAll,
    deleteItems,
    selectedFolderIds,
    selectedContentIds,
    active,
  ]);

  const handleCloseModal = (action: CreativeLibraryFolderModals) => {
    modals[action].toggle(false);
    clearAll();
  };

  return (
    <>
      {modals.create.isShown && (
        <NewFolderModal
          defaultDestinationFolderId={folderId}
          onClose={() => {
            handleCloseModal(CreativeLibraryFolderModals.Create);
          }}
          onConfirm={handleCreateFolderConfirmation}
          rootFolderId={rootFolderId}
          maxNameLength={MAX_FOLDER_NAME_LENGTH}
          treeViewQuery={useTreeViewQuery}
        />
      )}
      {modals.rename.isShown && (
        <RenameFolderModal
          folderName={selectedFolderData?.name || ''}
          onClose={() => {
            handleCloseModal(CreativeLibraryFolderModals.Rename);
          }}
          onConfirm={handleRenameFolder}
          isLoading={isLoadingRename}
          maxNameLength={MAX_FOLDER_NAME_LENGTH}
        />
      )}
      {modals.move.isShown && (
        <MoveToFolderModal
          currentFolderId={folderId}
          rootFolderId={rootFolderId}
          treeViewQuery={useTreeViewQuery}
          folderIds={selectedFolderIds}
          contentIds={selectedContentIds}
          contentItemName="asset"
          onClose={() => {
            handleCloseModal(CreativeLibraryFolderModals.Move);
          }}
          onConfirm={handleMoveToFolder}
        />
      )}
      {modals.delete.isShown && (
        <DeleteItemsModal
          onClose={() => {
            handleCloseModal(CreativeLibraryFolderModals.Delete);
          }}
          onConfirm={handleDeleteToFolder}
          displayTextForContentItems={getDeleteModalText(
            selectedFolders.length,
            seletedContent.length,
          )}
          content={seletedContent}
          folders={selectedFolders}
        />
      )}
    </>
  );
};
